import { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash, Pencil, Plus, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { CategoryService, Category, Subcategory } from '@/lib/category-service';
import { FormField } from '@/polymet/components/ui/form-field-wrapper';
import { useForm, Controller } from 'react-hook-form';
import { slugify } from '@/lib/utils';

const categoryService = new CategoryService();

interface SubcategoryFormData {
  name: string;
  slug: string;
  category_id: string;
  description?: string;
  image_url?: string;
  is_active: boolean;
  display_order?: number;
}

export function AdminSubcategoriesManager() {
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory | null>(null);
  const { toast } = useToast();
  
  const { register, handleSubmit, reset, setValue, control, formState: { errors } } = useForm<SubcategoryFormData>();

  // Load categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Load subcategories when a category is selected
  useEffect(() => {
    if (selectedCategoryId) {
      fetchSubcategories(selectedCategoryId);
    } else {
      setSubcategories([]);
      setIsLoading(false);
    }
  }, [selectedCategoryId]);

  const fetchCategories = async () => {
    try {
      const data = await categoryService.getCategories();
      setCategories(data);
      
      // Auto-select the first category if available
      if (data.length > 0 && !selectedCategoryId) {
        setSelectedCategoryId(data[0].id);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: 'Error',
        description: 'Failed to load categories. Please try again.',
        variant: 'destructive',
      });
      setIsLoading(false);
    }
  };

  const fetchSubcategories = async (categoryId: string) => {
    setIsLoading(true);
    try {
      const data = await categoryService.getSubcategoriesByCategoryId(categoryId);
      setSubcategories(data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      toast({
        title: 'Error',
        description: 'Failed to load subcategories. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    if (!selectedCategoryId) {
      toast({
        title: 'Error',
        description: 'Please select a category first.',
        variant: 'destructive',
      });
      return;
    }

    setSelectedSubcategory(null);
    reset({
      name: '',
      slug: '',
      category_id: selectedCategoryId,
      description: '',
      image_url: '',
      is_active: true,
      display_order: subcategories.length + 1
    });
    setIsFormDialogOpen(true);
  };

  const openEditDialog = (subcategory: Subcategory) => {
    setSelectedSubcategory(subcategory);
    reset({
      name: subcategory.name,
      slug: subcategory.slug,
      category_id: subcategory.category_id,
      description: subcategory.description || '',
      image_url: subcategory.image_url || '',
      is_active: subcategory.is_active !== false, // Default to true if undefined
      display_order: subcategory.display_order || 0
    });
    setIsFormDialogOpen(true);
  };

  const openDeleteDialog = (subcategory: Subcategory) => {
    setSelectedSubcategory(subcategory);
    setIsDeleteDialogOpen(true);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setValue('name', name);
    setValue('slug', slugify(name));
  };

  const handleCreateOrUpdateSubcategory = async (data: SubcategoryFormData) => {
    setIsSubmitting(true);
    try {
      if (selectedSubcategory) {
        // Update existing subcategory
        await categoryService.updateSubcategory(selectedSubcategory.id, data);
        toast({
          title: 'Success',
          description: 'Subcategory updated successfully.',
        });
      } else {
        // Create new subcategory
        await categoryService.createSubcategory(data);
        toast({
          title: 'Success',
          description: 'Subcategory created successfully.',
        });
      }
      setIsFormDialogOpen(false);
      fetchSubcategories(data.category_id);
    } catch (error) {
      console.error('Error saving subcategory:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save subcategory. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSubcategory = async () => {
    if (!selectedSubcategory) return;
    
    setIsSubmitting(true);
    try {
      const success = await categoryService.deleteSubcategory(selectedSubcategory.id);
      if (success) {
        toast({
          title: 'Success',
          description: 'Subcategory deleted successfully.',
        });
        setIsDeleteDialogOpen(false);
        fetchSubcategories(selectedSubcategory.category_id);
      } else {
        throw new Error('Failed to delete subcategory');
      }
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete subcategory. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Subcategories</h2>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Add Subcategory
        </Button>
      </div>

      <div className="mb-4">
        <Label htmlFor="category-select">Select Category</Label>
        <Select
          value={selectedCategoryId}
          onValueChange={setSelectedCategoryId}
        >
          <SelectTrigger className="w-full md:w-[300px] mt-1">
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : selectedCategoryId ? (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Order</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subcategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    No subcategories found for this category. Create your first subcategory.
                  </TableCell>
                </TableRow>
              ) : (
                subcategories.map((subcategory) => (
                  <TableRow key={subcategory.id}>
                    <TableCell className="font-medium">{subcategory.name}</TableCell>
                    <TableCell>{subcategory.slug}</TableCell>
                    <TableCell className="max-w-xs truncate">
                      {subcategory.description || 'No description'}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        subcategory.is_active !== false 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {subcategory.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>{subcategory.display_order || 0}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openEditDialog(subcategory)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openDeleteDialog(subcategory)}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-8 border rounded-md">
          <p className="text-muted-foreground">Please select a category to view its subcategories.</p>
        </div>
      )}

      {/* Subcategory Form Dialog */}
      <Dialog open={isFormDialogOpen} onOpenChange={setIsFormDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {selectedSubcategory ? 'Edit Subcategory' : 'Create New Subcategory'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(handleCreateOrUpdateSubcategory)}>
            <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
              <FormField
                label="Category"
                error={errors.category_id?.message}
                required
              >
                <Controller
                  name="category_id"
                  control={control}
                  rules={{ required: 'Category is required' }}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </FormField>
              
              <FormField
                label="Name"
                error={errors.name?.message}
                required
              >
                <Input
                  {...register('name', { 
                    required: 'Name is required',
                    onChange: handleNameChange
                  })}
                  placeholder="Subcategory name"
                />
              </FormField>
              
              <FormField
                label="Slug"
                error={errors.slug?.message}
                required
              >
                <Input
                  {...register('slug', { required: 'Slug is required' })}
                  placeholder="subcategory-slug"
                />
              </FormField>
              
              <FormField
                label="Description"
                error={errors.description?.message}
              >
                <Textarea
                  {...register('description')}
                  placeholder="Subcategory description"
                  rows={3}
                />
              </FormField>
              
              <FormField
                label="Image URL"
                error={errors.image_url?.message}
              >
                <Input
                  {...register('image_url')}
                  placeholder="https://example.com/image.jpg"
                />
              </FormField>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  {...register('is_active')}
                  defaultChecked={true}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              
              <FormField
                label="Display Order"
                error={errors.display_order?.message}
              >
                <Input
                  type="number"
                  {...register('display_order', {
                    valueAsNumber: true,
                  })}
                  placeholder="1"
                />
              </FormField>
            </div>
            
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {selectedSubcategory ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              Are you sure you want to delete the subcategory "{selectedSubcategory?.name}"?
              This action cannot be undone.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Note: Subcategories with product categories cannot be deleted. You must delete all product categories first.
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleDeleteSubcategory}
              disabled={isSubmitting}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
